
import React from 'react';
import { Palette, MessageSquare, ExternalLink, Flower, User, Zap, Sparkles, Star, Maximize2, CreditCard } from 'lucide-react';
import { Language, CommissionTier, ArtItem, ArtCategory, SiteSettings } from '../types';

interface CustomRequestProps {
  language: Language;
  onOpenChat: () => void;
  onPreview?: (item: ArtItem) => void;
  commissionTiers: CommissionTier[];
  settings: SiteSettings;
}

const CustomRequest: React.FC<CustomRequestProps> = ({ language, onOpenChat, onPreview, commissionTiers, settings }) => {
  const getIntroText = () => {
    if (!settings) return '';
    
    // 1. Prioridade para o idioma ativo selecionado
    if (language === 'pt' && settings.customRequestIntro_pt?.trim()) {
      return settings.customRequestIntro_pt.trim();
    }
    if (language === 'en' && settings.customRequestIntro_en?.trim()) {
      return settings.customRequestIntro_en.trim();
    }
    if (language === 'es' && settings.customRequestIntro_es?.trim()) {
      return settings.customRequestIntro_es.trim();
    }
    if (language === 'ru' && settings.customRequestIntro_ru?.trim()) {
      return settings.customRequestIntro_ru.trim();
    }

    // 2. Fallback inteligente para qualquer idioma que tenha sido configurado
    return (
      settings.customRequestIntro_pt?.trim() ||
      settings.customRequestIntro_en?.trim() ||
      settings.customRequestIntro_es?.trim() ||
      settings.customRequestIntro_ru?.trim() ||
      ''
    );
  };

  const dynamicIntro = getIntroText();

  const t = {
    pt: {
      title: 'Encomendas Exclusivas',
      sub: 'Obras Únicas sob Medida',
      desc: dynamicIntro || 'Deseja algo que transcenda a galeria comum? Entre em contato com nossos mestres artesãos para solicitar uma arte personalizada para o seu perfil.',
      niiko: 'Niiko',
      lucine: 'Lucine',
      contactTitle: 'Canais de Contato',
      steamProfile: 'Ver Perfil Steam',
      discordLabel: 'Discord Handle',
      quote: 'Cada pincelada é uma história única.',
      chatBtn: 'Chat',
      tiersTitle: 'Nossos Planos de Animação',
      requestBtn: 'Solicitar via Chat',
      simpleTitle: 'Simple Animation',
      simpleFeatures: ['Movimentos básicos', 'Efeitos sutis', 'Finalização rápida'],
      mediumTitle: 'Medium Animation',
      mediumFeatures: ['Movimento de personagem', 'Efeitos de partículas', 'Iluminação dinâmica'],
      fullTitle: 'Full Animation',
      fullFeatures: ['Rigging avançado', 'Cenário animado', 'Efeitos Premium', 'Prioridade máxima']
    },
    en: {
      title: 'Exclusive Commissions',
      sub: 'Unique Custom Works',
      desc: dynamicIntro || 'Looking for something beyond the common gallery? Contact our master craftsmen to request a personalized art for your profile.',
      niiko: 'Niiko',
      lucine: 'Lucine',
      contactTitle: 'Contact Channels',
      steamProfile: 'View Steam Profile',
      discordLabel: 'Discord Handle',
      quote: 'Every brushstroke is a unique story.',
      chatBtn: 'Chat',
      tiersTitle: 'Our Animation Tiers',
      requestBtn: 'Request via Chat',
      simpleTitle: 'Simple Animation',
      simpleFeatures: ['Basic movements', 'Subtle effects', 'Quick delivery'],
      mediumTitle: 'Medium Animation',
      mediumFeatures: ['Character movement', 'Particle effects', 'Dynamic lighting'],
      fullTitle: 'Full Animation',
      fullFeatures: ['Advanced Rigging', 'Animated background', 'Premium effects', 'Max priority']
    },
    es: {
      title: 'Encargos Exclusivos',
      sub: 'Obras Únicas a Medida',
      desc: dynamicIntro || '¿Buscas algo más allá de la galería común? Contacta con nuestros maestros artesanos para solicitar un arte personalizado para tu perfil.',
      niiko: 'Niiko',
      lucine: 'Lucine',
      contactTitle: 'Canales de Contacto',
      steamProfile: 'Ver Perfil de Steam',
      discordLabel: 'Usuario de Discord',
      quote: 'Cada trazo es una história única.',
      chatBtn: 'Chat',
      tiersTitle: 'Nuestros Planes de Animación',
      requestBtn: 'Solicitar por Chat',
      simpleTitle: 'Simple Animation',
      simpleFeatures: ['Movimientos básicos', 'Efectos sutiles', 'Entrega rápida'],
      mediumTitle: 'Medium Animation',
      mediumFeatures: ['Movimiento de personaje', 'Efectos de partículas', 'Iluminación dinâmica'],
      fullTitle: 'Full Animation',
      fullFeatures: ['Rigging avanzado', 'Fondo animado', 'Efectos Premium', 'Máxima prioridad']
    },
    ru: {
      title: 'Эксклюзивные Заказы',
      sub: 'Уникальные Работы на Заказ',
      desc: dynamicIntro || 'Ищете нечто большее, чем обычная галерея? Свяжитесь с нашими мастерами для заказа персонального арта для вашего профиля.',
      niiko: 'Niiko',
      lucine: 'Lucine',
      contactTitle: 'Контакты',
      steamProfile: 'Профиль Steam',
      discordLabel: 'Discord Ник',
      quote: 'Каждый мазок — уникальная история.',
      chatBtn: 'Чат',
      tiersTitle: 'Тарифы Анимации',
      requestBtn: 'Заказать в Чате',
      simpleTitle: 'Simple Animation',
      simpleFeatures: ['Базовые движения', 'Тонкие эффекты', 'Быстрая сдача'],
      mediumTitle: 'Medium Animation',
      mediumFeatures: ['Движение персонажа', 'Эффекты частиц', 'Динамический свет'],
      fullTitle: 'Full Animation',
      fullFeatures: ['Сложный риггинг', 'Анимированный фон', 'Премиум эффекты', 'Макс. приоритет']
    }
  }[language] || {
    title: 'Exclusive Commissions',
    sub: 'Unique Custom Works',
    desc: dynamicIntro || 'Looking for something beyond the common gallery? Contact our master craftsmen to request a personalized art for your profile.',
    niiko: 'Niiko',
    lucine: 'Lucine',
    contactTitle: 'Contact Channels',
    steamProfile: 'View Steam Profile',
    discordLabel: 'Discord Handle',
    quote: 'Every brushstroke is a unique story.',
    chatBtn: 'Chat',
    tiersTitle: 'Our Animation Tiers',
    requestBtn: 'Request via Chat',
    simpleTitle: 'Simple Animation',
    simpleFeatures: ['Basic movements', 'Subtle effects', 'Quick delivery'],
    mediumTitle: 'Medium Animation',
    mediumFeatures: ['Character movement', 'Particle effects', 'Dynamic lighting'],
    fullTitle: 'Full Animation',
    fullFeatures: ['Advanced Rigging', 'Animated background', 'Premium effects', 'Max priority']
  };

  const masters = [
    {
      name: t.niiko,
      discord: 'annaniiko',
      steam: 'https://steamcommunity.com/id/NiikoCute/',
      role: 'Master Artist',
      color: '#c084fc'
    },
    {
      name: t.lucine,
      discord: '_lucine',
      steam: 'https://steamcommunity.com/id/lucine87/',
      role: 'Master Designer',
      color: '#ffffff'
    }
  ];

  return (
    <div className="max-w-5xl mx-auto p-10 bg-zinc-900/40 backdrop-blur-xl border border-white/10 shadow-2xl hover:border-[#c084fc]/20 transition-all">
      <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-16 border-b border-white/5 pb-12">
        <div className="flex items-center gap-8">
          <div className="p-6 bg-white shadow-2xl border-b-8 border-[#c084fc]">
            <Palette className="w-12 h-12 text-black" />
          </div>
          <div className="text-center md:text-left">
            <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter oil-text">
              {t.title}
            </h2>
            <p className="text-zinc-500 font-bold uppercase text-xs tracking-[0.4em] mt-2">{t.sub}</p>
          </div>
        </div>
        
        <button 
          onClick={onOpenChat}
          className="bg-[#c084fc] hover:bg-white text-white hover:text-black font-black px-10 py-5 flex items-center gap-4 transition-all shadow-[0_15px_40px_rgba(192,132,252,0.2)] active:scale-95 border-b-4 border-white/20 uppercase tracking-widest text-xs"
        >
          <Zap className="w-5 h-5 animate-pulse" />
          {t.chatBtn}
        </button>
      </div>

      <p className="text-zinc-400 text-xl leading-relaxed mb-16 max-w-3xl mx-auto italic text-center whitespace-pre-wrap">
        "{t.desc}"
      </p>

      {/* Animation Tiers Section */}
      <div className="mb-20">
        <h3 className="text-2xl font-black text-white uppercase tracking-tighter mb-8 flex items-center gap-4">
          <Sparkles className="w-6 h-6 text-[#c084fc]" />
          {t.tiersTitle}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { 
              id: 'simple',
              title: t.simpleTitle, 
              icon: Zap, 
              color: 'text-zinc-400',
              borderColor: 'border-zinc-800'
            },
            { 
              id: 'full',
              title: t.fullTitle, 
              icon: Star, 
              color: 'text-yellow-500',
              borderColor: 'border-yellow-500/20',
              highlight: true
            },
            { 
              id: 'medium',
              title: t.mediumTitle, 
              icon: Sparkles, 
              color: 'text-[#c084fc]',
              borderColor: 'border-[#c084fc]/30'
            }
          ].map((styleTier, idx) => {
            const dynamicTier = Array.isArray(commissionTiers) ? commissionTiers.find(t => t.id === styleTier.id) : null;
            const demoUrl = dynamicTier?.demoUrl || (styleTier.id === 'simple' ? 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExM3YybXN1N3B6NGo3Z3R4bnlqZ3R4bnlqZ3R4bnlqZ3R4bnlqZ3R4bnkmZXA9djFfaW50ZXJuYWxfZ2lmX2J5X2lkJmN0PWc/3o7TKMG78pxg1I59V6/giphy.mp4' : styleTier.id === 'medium' ? 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExaTN3NWJtMm1rbXg2cjAwbHNmdmZkaDg2M2d4NzFkOWd0OTlwc3Q3NyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/JDtoKaAUiTDxjnSj7s/giphy.mp4' : 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExMmxpZTNubmEyaDFobm9ndHJpbmR2NW0xeG1pZnhrYnZ2djB6bW9reCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/l0HlS6yLsc5t155wA/giphy.mp4');

            return (
              <div 
                key={idx} 
                className={`bg-zinc-950/60 border ${styleTier.borderColor} flex flex-col group hover:bg-zinc-900/60 transition-all cursor-pointer ${styleTier.highlight ? 'ring-1 ring-[#c084fc]/20 scale-105 z-10' : ''}`} 
                onClick={() => {
                  if (onPreview) {
                    const mockArt: ArtItem = {
                      id: `tier-${styleTier.id}`,
                      title: {
                        pt: styleTier.title,
                        en: styleTier.title
                      },
                      category: ArtCategory.ANIMATED,
                      price: dynamicTier?.price || 0,
                      imageUrl: '', // Fallback image would be used
                      videoUrl: demoUrl,
                      description: {
                        pt: `Demonstração do plano ${styleTier.title}${dynamicTier?.price ? ` - $ ${dynamicTier.price.toFixed(2)}${dynamicTier.maxPrice ? ` - ${dynamicTier.maxPrice.toFixed(2)}` : ''}` : ''}`,
                        en: `Demo for ${styleTier.title} tier${dynamicTier?.price ? ` - $ ${dynamicTier.price.toFixed(2)}${dynamicTier.maxPrice ? ` - ${dynamicTier.maxPrice.toFixed(2)}` : ''}` : ''}`
                      },
                      isAnimated: true,
                      notForSale: true
                    };
                    onPreview(mockArt);
                  } else {
                    onOpenChat();
                  }
                }}
              >
                {/* Demo Space */}
                <div className="aspect-[9/14] bg-black overflow-hidden relative group/demo">
                  <video 
                    src={demoUrl} 
                    autoPlay loop muted playsInline 
                    className="w-full h-full object-cover transition-all duration-700 opacity-60 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent opacity-60" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <Maximize2 className="w-8 h-8 text-white shadow-2xl" />
                  </div>
                </div>

                <div className="p-6 flex flex-col items-center justify-between text-center flex-1">
                  <div className="flex flex-col items-center text-center">
                    <h4 className="text-sm font-black text-white uppercase tracking-widest">{styleTier.title}</h4>
                    {dynamicTier?.price && dynamicTier.price > 0 && (
                      <span className="text-[#c084fc] font-mono text-xs font-black mt-2">
                        $ {dynamicTier.price.toFixed(2)}
                        {dynamicTier.maxPrice && dynamicTier.maxPrice > dynamicTier.price && (
                          <> - {dynamicTier.maxPrice.toFixed(2)}</>
                        )}
                      </span>
                    )}
                    {dynamicTier?.description && (
                      <p className="text-[10px] text-zinc-500 mt-4 leading-relaxed max-w-[200px] text-center whitespace-pre-wrap">
                        {dynamicTier.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="w-full mt-6">
                    {dynamicTier && (dynamicTier.stripeUrl || dynamicTier.stripeUrlBr) ? (
                      <div className="flex flex-col gap-2 w-full">
                        {dynamicTier.stripeUrlBr && (
                          <a 
                            href={dynamicTier.stripeUrlBr}
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="bg-[#c084fc] hover:bg-white text-white hover:text-black font-black px-4 py-3 flex items-center justify-center gap-2 transition-all shadow-[0_4px_12px_rgba(192,132,252,0.25)] hover:shadow-[0_4px_20px_rgba(192,132,252,0.4)] active:scale-95 border-b-2 border-white/20 uppercase tracking-widest text-[9px] w-full"
                          >
                            <CreditCard className="w-3.5 h-3.5" />
                            {language === 'pt' ? 'Pagar com Stripe (BRL / PIX)' : 'Pay with Stripe (BRL / PIX)'}
                          </a>
                        )}
                        {dynamicTier.stripeUrl && (
                          <a 
                            href={dynamicTier.stripeUrl}
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="bg-zinc-900 hover:bg-white text-zinc-300 hover:text-black font-black px-4 py-3 flex items-center justify-center gap-2 transition-all border border-zinc-800 hover:border-white active:scale-95 uppercase tracking-widest text-[9px] w-full"
                          >
                            <CreditCard className="w-3.5 h-3.5" />
                            {language === 'pt' ? 'Pagar com Stripe (USD / Paypal)' : 'Pay with Stripe (USD / Paypal)'}
                          </a>
                        )}
                      </div>
                    ) : (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenChat();
                        }}
                        className="bg-zinc-900 hover:bg-[#c084fc] text-zinc-400 hover:text-white font-black px-4 py-3 flex items-center justify-center gap-2 transition-all border border-zinc-800 hover:border-[#c084fc]/40 active:scale-95 uppercase tracking-widest text-[9px] w-full"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        {language === 'pt' ? 'Fazer Pedido no Chat' : 'Order via Chat'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
        {masters.map((master, idx) => (
          <div key={idx} className="bg-zinc-950/80 border border-white/5 p-8 group hover:border-[#c084fc]/40 transition-all relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#c084fc]/5 -translate-y-16 translate-x-16 rounded-full group-hover:scale-150 transition-transform" />
            
            <div className="flex items-center gap-6 mb-8 relative z-10">
              <div className="w-16 h-16 bg-white flex items-center justify-center border-b-4 border-[#c084fc]">
                <User className="w-8 h-8 text-black" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-white uppercase tracking-tighter artistic-font">{master.name}</h3>
                <span className="text-[10px] text-zinc-500 font-black uppercase tracking-widest">{master.role}</span>
              </div>
            </div>

            <div className="space-y-6 relative z-10">
              <div className="bg-black/40 p-5 border border-white/5 group-hover:border-[#c084fc]/20 transition-all">
                <span className="text-[9px] font-black text-zinc-600 uppercase tracking-widest block mb-2">{t.discordLabel}</span>
                <div className="flex items-center justify-between">
                  <span className="text-white font-mono text-lg font-bold">@{master.discord}</span>
                  <div className="w-8 h-8 bg-zinc-900 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-[#c084fc]" />
                  </div>
                </div>
              </div>

              <a 
                href={master.steam} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full bg-zinc-900 hover:bg-white text-zinc-500 hover:text-black py-4 px-6 flex items-center justify-between transition-all border border-zinc-800 hover:border-transparent group/link"
              >
                <span className="text-[10px] font-black uppercase tracking-widest">{t.steamProfile}</span>
                <ExternalLink className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-10 border-t border-zinc-800 flex justify-between items-center">
        <p className="text-sm text-zinc-700 font-black uppercase tracking-widest">{t.quote}</p>
        <Flower className="w-6 h-6 text-[#c084fc] opacity-20" />
      </div>
    </div>
  );
};

export default CustomRequest;
